import {
  GenerationOutcomeUnknownError,
  serverGenerateWithRetry,
  type ServerGenerateResponse,
} from '../../services/geminiTransport';
import type { MajorSceneOperation, SceneTransportDiagnostic } from '../types';
import { SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS } from './scribeThinkingPolicy.service';

const BASE_DELAY_MS = 3_000;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function isSceneTransportRetryable(error: unknown): boolean {
  if (error instanceof GenerationOutcomeUnknownError) return false;
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: string; message?: string; status?: number };
  const message = (err.message ?? '').toLowerCase();

  if (err.name === 'AbortError' || err.name === 'TypeError' || message.includes('failed to fetch')) {
    return true;
  }
  if (err.status === 429) return true;
  if (typeof err.status === 'number' && err.status >= 500) return true;
  if (message.includes('502') || message.includes('503') || message.includes('504')) return true;
  if (message.includes('bad gateway') || message.includes('temporarily failed')) return true;

  return false;
}

export function isNarrativeSceneFailure(_error: unknown): boolean {
  return false;
}

export interface MajorSceneTransportContext {
  logicalSceneCallId: string;
  sceneId: string;
  sceneAttempt: number;
  operation: MajorSceneOperation;
}

export interface MajorSceneTransportCallbacks {
  onDiagnostic?: (diagnostic: SceneTransportDiagnostic) => void;
}

/**
 * Bounded transport for MAJOR scene operations.
 * Client: max 2 HTTP attempts. Server: max 1 Gemini upstream per HTTP request.
 * Narrative shortfalls must use targeted scene retry, not transport retry.
 */
export async function generateMajorSceneWithBoundedTransport(
  params: Record<string, unknown>,
  ctx: MajorSceneTransportContext,
  callbacks?: MajorSceneTransportCallbacks,
): Promise<ServerGenerateResponse & { transportAttemptsUsed: number }> {
  let lastError: unknown;

  for (let transportAttempt = 1; transportAttempt <= SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS; transportAttempt++) {
    callbacks?.onDiagnostic?.({
      logicalSceneCallId: ctx.logicalSceneCallId,
      sceneId: ctx.sceneId,
      sceneAttempt: ctx.sceneAttempt,
      transportAttempt,
      serverUpstreamAttempt: 1,
      generationPolicy: 'major_scene',
      operation: ctx.operation,
    });

    try {
      const response = await serverGenerateWithRetry(params, {
        policy: 'major_scene',
        operation: ctx.operation,
        agent: 'scribe',
        maxRetries: 1,
      });
      return { ...response, transportAttemptsUsed: transportAttempt };
    } catch (error: unknown) {
      lastError = error;
      if (!isSceneTransportRetryable(error) || transportAttempt >= SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS) {
        throw error;
      }
      const backoff = BASE_DELAY_MS * transportAttempt;
      console.warn(
        `[majorSceneTransport] Retryable transport failure for ${ctx.logicalSceneCallId} (transport ${transportAttempt}/${SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS}). Retrying in ${backoff}ms.`,
      );
      await delay(backoff);
    }
  }

  throw lastError;
}
